import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-energy-flow',
    category: 'results',
    title: 'Čtení grafu Energetická bilance',
    summary: 'Jak číst hodinový graf zobrazující každý zdroj a spotřebu energie po optimalizaci.',
    body: (
      <>
        <p>
          Graf <strong>Energetická bilance</strong> se zobrazuje pod souhrnem KPI na stránce
          Optimalizace (výsledky). Jde o hodinový plošný graf s jednou řadou pro každý druh zdroje
          nebo spotřeby, přepínatelný mezi zobrazením za celé společenství a za jednotlivé budovy.
        </p>
        <ScreenshotPlaceholder
          img="energy-flow.png"
          description="The Energy Balance area chart over 24 hours — stacked series for Base Load, Flexible Load, PV Generation (or Rooftop PV), Wind Generation, Grid Import, Surplus, Battery Charge and Battery Discharge."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Scroll below the KPI Summary to the Energy Balance chart and capture the full 24-hour area chart."
          caption="Energetická bilance za optimalizovaný den."
        />

        <h5 className="mt-3">Řady, které uvidíte</h5>
        <p>Graf zobrazuje až tyto řady — zobrazí se pouze ty, které jsou relevantní pro vaše aktiva:</p>
        <ul>
          <li><strong>Základní zatížení</strong> — pevná spotřeba, která musí být pokryta každou hodinu.</li>
          <li><strong>Flexibilní zatížení</strong> — spotřeba, kterou může optimalizátor časově přesouvat.</li>
          <li><strong>Výroba z FV</strong> (za společenství) nebo <strong>Střešní FV</strong> (za budovu).</li>
          <li><strong>Alokace komunitní výroby</strong> — podíl komunitní výroby směrovaný do dané budovy (pouze v zobrazení za budovu).</li>
          <li><strong>Výroba z větru</strong> — výkon jakékoli komunitní větrné turbíny.</li>
          <li><strong>Odběr ze sítě</strong> — energie odebraná ze sítě v dané hodině.</li>
          <li><strong>Přebytek</strong> — výroba, která nebyla v dané hodině ani spotřebována, ani uložena (dodána do sítě nebo omezena).</li>
          <li><strong>Nabíjení baterie</strong> a <strong>Vybíjení baterie</strong> — příspěvek baterie za každou hodinu.</li>
        </ul>

        <h5 className="mt-3">Vzory, které něco signalizují</h5>
        <ul>
          <li><strong>Vysoká Výroba z FV uprostřed dne + blok Přebytku</strong> — solární výroba převýšila spotřebu společenství; bez větší baterie se zbytek dodá do sítě nebo omezí.</li>
          <li><strong>Výroba z FV v poledne + Nabíjení baterie ve stejnou hodinu</strong> — optimalizátor šetří přebytek na večerní špičku.</li>
          <li><strong>Vybíjení baterie večer + malý Odběr ze sítě</strong> — baterie plní svůj účel; ve špičkových cenách je potřeba jen málo drahé energie ze sítě.</li>
          <li><strong>Odběr ze sítě přes noc + Nabíjení baterie ve stejnou dobu</strong> — dává smysl pouze tehdy, jsou-li noční tarify velmi levné; jinak zkontrolujte soubor tarifu.</li>
        </ul>
        <Tip>Porovnejte s <ArticleLink to="results-battery">křivkou stavu nabití baterie</ArticleLink> — obě říkají totéž ze dvou různých úhlů pohledu.</Tip>

        <h5 className="mt-3">Roční energetické toky (Dlouhodobé plánování)</h5>
        <p>
          V výsledcích <em>Dlouhodobého plánování</em> se zobrazuje odlišný graf s podobným názvem —
          sloupcový graf <strong>Roční energetické toky</strong>. Každý sloupec odpovídá jednomu roku
          projekce: kladné hodnoty = spotřebovaná nebo odebraná energie, záporné hodnoty = energie
          dodaná do sítě. Použijte jej k zobrazení vývoje energetického mixu po dobu životnosti
          investice.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-battery',
    category: 'results',
    title: 'Čtení výsledků baterie',
    summary: 'Co říká křivka stavu nabití a řady nabíjení/vybíjení baterie.',
    body: (
      <>
        <p>
          Pokud je baterie součástí vašeho společenství, optimalizátor zobrazí pohled
          {' '}<strong>Plán baterie</strong> s <strong>křivkou stavu nabití (SOC)</strong>,
          která ukazuje, jak plná je baterie v každé hodině, spolu s odpovídajícími řadami nabíjení
          a vybíjení v grafu{' '}<ArticleLink to="results-energy-flow">Energetická bilance</ArticleLink>.
        </p>
        <ScreenshotPlaceholder
          img="battery-soc.png"
          description="The state-of-charge curve — 0% to 100% on the Y axis, hours on the X axis, typically a daily up-down pattern between the configured Min SOC and Max SOC bounds (defaults 10% and 90%)."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run on a community that has a battery asset. Scroll to the Battery Schedule section and capture the SOC line chart."
          caption="Stav nabití baterie za optimalizovaný den."
        />

        <h5 className="mt-3">Zdravý denní vzor</h5>
        <ul>
          <li>SOC roste během slunečných hodin (nabíjení z FV) nebo během hodin levného tarifu.</li>
          <li>SOC klesá ve večerní špičce (vybíjení do zátěže, čímž se nahrazuje drahý odběr ze sítě).</li>
          <li>SOC zůstává v nakonfigurovaných mezích — výchozí hodnoty jsou <strong>Min 10 %</strong> a
            <strong> Max 90 %</strong>, nastavené na aktivu baterie.</li>
          <li>Křivka by neměla po mnoho hodin ležet na minimu ani maximu — to naznačuje, že baterie
            je buď příliš malá, nebo málo využívaná.</li>
        </ul>

        <h5 className="mt-3">Nastavení baterie ke kontrole (na aktivu)</h5>
        <p>
          Chování baterie v optimalizaci je dáno těmito nastaveními na stránce aktiva společenství
          nebo budovy:
        </p>
        <ul>
          <li><strong>Kapacita (kWh)</strong> — kolik energie se vejde dovnitř.</li>
          <li><strong>Rychlost nabíjení / vybíjení (kW)</strong> — jak rychle může přesouvat energii dovnitř a ven.</li>
          <li><strong>Účinnost zpátečního cyklu (%)</strong> — energie na výstupu dělená energií na vstupu. Výchozí
            hodnota je 90 %. Realistická hodnota pro Li-ion v praxi je 85–95 % — výrazně nižší hodnota znamená,
            že ztráty jsou modelovány velmi agresivně.</li>
          <li><strong>Min / Max SOC (%)</strong> — provozní okno, výchozí hodnota 10 % až 90 %.</li>
          <li><strong>Počáteční SOC (%)</strong> — výchozí stav baterie (výchozí hodnota 50 %).</li>
        </ul>

        <h5 className="mt-3">Čtení plánu vedle křivky SOC</h5>
        <p>
          Porovnejte křivku SOC s řadami Nabíjení baterie / Vybíjení baterie v grafu
          {' '}<ArticleLink to="results-energy-flow">Energetická bilance</ArticleLink>: každá hodina
          „nabíjení" by měla SOC zvýšit, každá hodina „vybíjení" by ho měla snížit. Skok SOC bez
          odpovídajícího nabíjení v plánu znamená, že data byla mezi zobrazeními upravena — v rámci
          jednoho běhu by k tomu nemělo docházet.
        </p>
        <Warn>
          Baterie, která po mnoho hodin setrvává na 100 % (nebo na hodnotě Max SOC), nepracuje —
          buď je FV předimenzované vzhledem k zátěži, tarif nemá žádné využitelné cenové rozpětí,
          nebo cíl / omezení brání vybíjení. Vyzkoušejte cíl <em>Minimalizovat náklady</em>
          s reálným tariféem Day-Ahead a sledujte, zda se baterie stane aktivnější.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-cba',
    category: 'results',
    title: 'Čtení výsledků Dlouhodobého plánování (CBA)',
    summary: 'NPV, BCR, návratnost, roční úspory, CO₂ — co každé číslo znamená a jak porovnávat scénáře.',
    body: (
      <>
        <p>
          <strong>Dlouhodobé plánování</strong> porovnává jeden nebo více investičních scénářů
          (nové FV, nová baterie, nové nabíjecí stanice pro EV atd.) se <em>základním scénářem</em>
          nečinnosti, v horizontu, který si zvolíte (obvykle 5, 10 nebo 20 let). Výsledkem je
          srovnávací tabulka finančních KPI doplněná grafy.
        </p>
        <ScreenshotPlaceholder
          img="cba-scenarios.png"
          description="The CBA scenario comparison — one row per scenario showing NPV, BCR, payback, annual savings, and CO₂ avoided per year. The best-financial scenario is highlighted, and the best-environmental scenario (if different) is badged."
          source="Platform — open a community → click 'Past Results' (or Planning Tools) → run a Long-Term Planning scenario → open its results. Capture the scenario comparison panel near the top of the page."
          caption="Porovnání scénářů z Dlouhodobého plánování."
        />

        <h5 className="mt-3">Co znamená každý sloupec</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Sloupec</th><th>Srozumitelný popis</th><th>Dobré, pokud…</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>NPV</strong></td><td>Celkový zisk (v dnešních penězích) po dobu životnosti projektu po uplatnění diskontní sazby.</td><td>Kladné (a co nejvyšší).</td></tr>
            <tr><td><strong>BCR</strong></td><td>Poměr přínosů a nákladů: celkové přínosy dělené celkovými náklady.</td><td>Nad 1,0.</td></tr>
            <tr><td><strong>Návratnost (roky)</strong></td><td>Počet let, než kumulativní úspory pokryjí počáteční investici.</td><td>Kratší než životnost aktiva (např. solární panely vydrží 20–25 let).</td></tr>
            <tr><td><strong>Roční úspory</strong></td><td>Průměrné roční úspory oproti základnímu scénáři (nebo roční náklady na odběr ze sítě pro řádek základního scénáře).</td><td>Čím vyšší, tím lepší — ale porovnejte s počáteční investicí.</td></tr>
            <tr><td><strong>Ušetřené CO₂ / rok</strong></td><td>Tuny CO₂ nevyprodukované za rok oproti základnímu scénáři (používá faktor CO₂ sítě vašeho společenství).</td><td>Čím vyšší, tím lepší; užitečné pro výkaznictví v oblasti udržitelnosti.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Poznámka k diskontní sazbě</h5>
        <p>
          Stránka nabízí vstupní pole <strong>Diskontní sazba</strong> (výchozí hodnota 8 %) a
          citlivostní tabulku. NPV se počítá pro zadanou sazbu. Zkuste změnit hodnotu o ±2 procentní
          body a sledujte, jak citlivé je váš závěr — viz tabulka <em>Citlivost diskontní sazby</em>
          na téže stránce.
        </p>

        <h5 className="mt-3">Jak porovnávat scénáře</h5>
        <ol>
          <li>Začněte hodnotou <strong>NPV</strong> — jde o nejlepší finanční shrnutí. Scénář s nejvyšším NPV je finančním vítězem a na stránce je zvýrazněn.</li>
          <li>Ověřte pomocí <strong>BCR</strong> a <strong>návratnosti</strong> — pokud je NPV vysoké, ale návratnost je 18 let u aktiva s životností 20 let, případ je citlivý na předpoklady.</li>
          <li>Pokud se dva scénáře shodují v NPV, upřednostněte vyšší BCR nebo nižší počáteční investici.</li>
          <li>Hodnotu <strong>Ušetřené CO₂</strong> porovnejte zvlášť — nejlepší environmentální scénář se může lišit od nejlepšího finančního a stránka ho odpovídajícím způsobem označí.</li>
        </ol>

        <Warn>
          Kladné NPV závisí na vstupních předpokladech — inflaci cen elektřiny, diskontní sazbě,
          životnosti aktiv. Pomocí citlivostní tabulky na stránce ověřte, zda NPV zůstane kladné
          při mírné změně diskontní sazby; pokud snadno přejde do záporných hodnot, případ není
          robustní.
        </Warn>
        <Tip>
          Stránka s výsledky platformy zobrazuje nejlepší finanční a nejlepší environmentální scénář
          jako souhrnné přehledy. Používejte je pro rychlý přehled, ale před doporučením investice
          vždy zkontrolujte celou tabulku.
        </Tip>
      </>
    ),
  },
]
